import React, { useEffect, useMemo, useRef } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { createClient, getFaxTemplates } from './CustomerView.actions';
import Spinner from 'components/Spinner';
import { showNotification } from 'services/NotificationService';
import { useConfigForm } from '../configTool/useConfigForm';
import { validateClientCreateForm } from '../configTool/configFormValidators';
import { ClientFormSections } from '../configTool/ClientFormSections';
import ConfigFormProgress from '../configTool/ConfigFormProgress';
import { useUnsavedChangesWarning } from '../configTool/useUnsavedChangesWarning';
import { DEFAULT_LOGO_FILE } from '../configTool/LogoUrlField';
import './CreateClientModal.css';

const EMPTY_CLIENT_FORM = {
  clientName: '',
  programName: '',
  clientNameAbbreviation: '',
  logoUrl: DEFAULT_LOGO_FILE,
  supportNumber: '',
  faxNumberInbound: '',
  hasCustomSwitches: false,
  isPrescriberEmailEnabled: false,
  isRxAdvocateEnabled: false,
  prescriberCallActivitiesEnabled: false,
  pharmacyCallActivitiesEnabled: false,
  useNvpSourcePricing: false,
  excludeStarSwitches: false,
  excludeStarDeprescriptions: false,
  polypharmacyEnabled: false,
  deprescriptionConfirmationEnabled: false,
  populationType: '',
  language: '',
  faxTemplate: '',
};

const REQUIRED_FIELDS = [
  { name: 'clientName', isComplete: (s) => Boolean(String(s.clientName || '').trim()) },
  { name: 'programName', isComplete: (s) => Boolean(String(s.programName || '').trim()) },
  {
    name: 'clientNameAbbreviation',
    isComplete: (s) => Boolean(String(s.clientNameAbbreviation || '').trim()),
  },
  { name: 'logoUrl', isComplete: (s) => Boolean(String(s.logoUrl || '').trim()) },
  { name: 'language', isComplete: (s) => Boolean(String(s.language || '').trim()) },
  { name: 'faxTemplate', isComplete: (s) => Boolean(String(s.faxTemplate || '').trim()) },
  { name: 'populationType', isComplete: (s) => Boolean(String(s.populationType || '').trim()) },
];

const CreateClientModal = ({ show, onHide, customerInfoUuid, customerName, onSuccess }) => {
  const dispatch = useDispatch();

  const {
    creatingClient,
    createClientError,
    faxTemplates,
    loadingFaxTemplates,
  } = useSelector((state) => state.customerView);

  const initialValues = useMemo(() => ({ ...EMPTY_CLIENT_FORM }), []);

  const {
    formState,
    errors,
    isDirty,
    handleInputChange,
    handleCheckboxChange,
    handleBlur,
    shouldShowError,
    resetForm,
    runValidation,
  } = useConfigForm({
    initialValues,
    validate: validateClientCreateForm,
  });

  const { confirmDiscard } = useUnsavedChangesWarning(show && isDirty);
  const formInitializedRef = useRef(false);

  useEffect(() => {
    if (show) {
      dispatch(getFaxTemplates());
    } else {
      formInitializedRef.current = false;
    }
  }, [dispatch, show]);

  useEffect(() => {
    if (!show || loadingFaxTemplates || formInitializedRef.current) {
      return;
    }
    resetForm({ ...EMPTY_CLIENT_FORM });
    formInitializedRef.current = true;
  }, [show, loadingFaxTemplates, resetForm]);

  const requiredProgress = useMemo(() => {
    if (!formState) return { completed: 0, total: REQUIRED_FIELDS.length };
    const completed = REQUIRED_FIELDS.filter((f) => f.isComplete(formState)).length;
    return { completed, total: REQUIRED_FIELDS.length };
  }, [formState]);

  const handleSave = () => {
    if (!runValidation()) {
      return;
    }
    dispatch(createClient(customerInfoUuid, formState))
      .then((result) => {
        if (result.type === 'CREATE_CLIENT_SUCCESS') {
          showNotification({
            title: 'Client created successfully!',
            message: 'The client for this customer is created successfully',
            position: 'tr',
            type: 'success',
          });
          onSuccess();
          handleClose(true);
        } else {
          showNotification({
            title: 'Error creating client.',
            position: 'tr',
            type: 'error',
          });
        }
      });
  };

  const handleClose = (skipConfirm = false) => {
    if (!skipConfirm && !confirmDiscard()) {
      return;
    }
    resetForm({ ...EMPTY_CLIENT_FORM });
    dispatch({ type: 'CREATE_CLIENT_FAILURE', payload: null });
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={() => handleClose(false)}
      size="lg"
      backdrop="static"
      keyboard={false}
      className="create-client-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Create Client for {customerName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loadingFaxTemplates || !formState ? <Spinner /> : (
          <>
            {createClientError && <Alert variant="danger">An error occurred while creating the client.</Alert>}
            <Form>
              <ClientFormSections
                formState={formState}
                errors={errors}
                onInputChange={handleInputChange}
                onCheckboxChange={handleCheckboxChange}
                onBlur={handleBlur}
                shouldShowError={shouldShowError}
                faxTemplates={faxTemplates}
                loadingFaxTemplates={loadingFaxTemplates}
                disabledFields={{ callActivities: true }}
              />
            </Form>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="create-client-modal__footer">
        <ConfigFormProgress
          completed={requiredProgress.completed}
          total={requiredProgress.total}
        />
        <div className="d-flex">
          <Button variant="secondary" onClick={() => handleClose(false)} className="mr-2">
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={creatingClient || requiredProgress.completed < requiredProgress.total}
          >
            {creatingClient ? 'Creating...' : 'Create Client'}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateClientModal;
